/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useIntl } from 'react-intl';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Portlet,
  PortletContent,
  PortletHeader,
  PortletLabel,
  PortletToolbar,
} from 'components';
import { Download, ShareButton } from 'components/Download';
// import moment from 'moment';
// import { parse } from 'date-fns';

const TITLES = {
  PST: 'Flicker Short Term',
  PLT: 'Flicker Long Term',
  THDC: 'THD Corriente',
  THDV: 'THD Voltaje',
};

export default function TenMinutalChart({ title, data }) {
  const [dataChart, setDataChart] = useState([]);
  const chartRef = useRef(null);
  const intl = useIntl();
  const colors = ['#333333', '#FF4A4A', '#00AFEF'];

  // const parsed = (date) => parse(date, 'yyyyMMddHHmm', new Date());
  // Obtiene los puntos mas altos
  const getMaxPoint = (key) => {
    const values = dataChart
      .map((d) => ({
        time: d.datetime.getTime(),
        value: d[key],
      }))
      .filter((v) => v.value !== null && v.value !== undefined);

    const max = Math.max(...values.map((v) => v.value));
    return values.find((v) => v.value === max);
  };
  // Obtiene los puntos mas bajos
  const getMinPoint = (key) => {
    const values = dataChart
      .map((d) => ({
        time: d.datetime.getTime(),
        value: d[key],
      }))
      .filter((v) => v.value !== null && v.value !== undefined);

    const min = Math.min(...values.map((v) => v.value));
    return values.find((v) => v.value === min);
  };

  // Constantes maximas por fases
  const maxL1 = getMaxPoint('l1');
  const maxL2 = getMaxPoint('l2');
  const maxL3 = getMaxPoint('l3');

  // Constantes minimas por fases
  const minL1 = getMinPoint('l1');
  const minL2 = getMinPoint('l2');
  const minL3 = getMinPoint('l3');

  const scatterSeries = [
    {
      type: 'scatter',
      name: 'Minímo',
      data: [],
      color: '#fff',
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: '#fff',
        lineColor: '#000',
        lineWidth: 1,
      },
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo',
      data: [],
      color: '#fff',
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: '#fff',
        lineColor: '#000',
        lineWidth: 1,
      },
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 1',
      data: maxL1 ? [[maxL1.time, maxL1.value]] : [],
      color: colors[0],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[0],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 1',
      data: minL1 ? [[minL1.time, minL1.value]] : [],
      color: colors[0],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[0],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 2',
      data: maxL2 ? [[maxL2.time, maxL2.value]] : [],
      color: colors[1],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[1],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 2',
      data: minL2 ? [[minL2.time, minL2.value]] : [],
      color: colors[1],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[1],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Minímo Fase 3',
      data: minL3 ? [[minL3.time, minL3.value]] : [],
      color: colors[2],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle-down',
        fillColor: colors[2],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
    {
      type: 'scatter',
      name: 'Máximo Fase 3',
      data: maxL3 ? [[maxL3.time, maxL3.value]] : [],
      color: colors[2],
      marker: {
        enabled: true,
        radius: 6,
        symbol: 'triangle',
        fillColor: colors[2],
        lineColor: '#000',
        lineWidth: 1,
      },
      enableMouseTracking: true,
      showInLegend: false,
      tooltip: {
        enabled: true,
      },
    },
  ];

  // 🌍 Set global Highcharts options on mount
  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        months: [...Array(12)].map((_, i) =>
          intl.formatMessage({
            id: `app.months.${['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dec'][i]}`,
          })
        ),
        weekdays: [...Array(7)].map((_, i) =>
          intl.formatMessage({
            id: `app.daysOfWeek.${['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][i]}`,
          })
        ),
        shortMonths: [...Array(12)].map((_, i) =>
          intl.formatMessage({
            id: `app.monthsShort.${['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'][i]}`,
          })
        ),
        shortWeekdays: [...Array(7)].map((_, i) =>
          intl.formatMessage({
            id: `app.daysOfWeekShort.${['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][i]}`,
          })
        ),
      },
      time: {
        useUTC: true,
      },
      credits: { enabled: false },
    });
  }, [intl]);

  useEffect(() => {
    if (!data.length) return setDataChart([]);

    let key;
    switch (title) {
      case TITLES.PST:
        key = 'flicker_Pst';
        break;
      case TITLES.PLT:
        key = 'flicker_Plt';
        break;
      case TITLES.THDC:
        key = 'thd_corriente';
        break;
      case TITLES.THDV:
        key = 'thd_voltaje';
        break;
      default:
        return setDataChart([]);
    }

    const formatted = data
      .map((element) => {
        const date = new Date(element?.timestamp);
        return {
          datetime: date,
          l1: element?.mediciones?.[key]?.[0],
          l2: element?.mediciones?.[key]?.[1],
          l3: element?.mediciones?.[key]?.[2],
        };
      })
      .sort((a, b) => a.datetime - b.datetime);

    setDataChart(formatted);
  }, [data, title]);

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'line',
        zoomType: 'x',
        height: 450,
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: 'Tiempo',
        },
        labels: {
          format: '{value:%H:%M}',
        },
      },
      tooltip: {
        shared: true,
        xDateFormat: '%A, %b %e, %H:%M:%S UTC',
        distance: 30,
        split: true,
        stickOnContact: true,
        positioner: function (labelWidth, labelHeight, point) {
          const chart = this.chart;
          let x = point.plotX + chart.plotLeft;
          let y = point.plotY + chart.plotTop - 100;
          if (x + labelWidth > chart.chartWidth)
            x = chart.chartWidth - labelWidth - 10;
          if (y < 0) y = 50;
          return { x, y };
        },
        formatter: function () {
          const date = new Date(this.x);
          const locale = intl.formatMessage({ id: 'app.locale' });
          const options = {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: 'numeric',
            minute: 'numeric',
            weekday: 'long',
            timeZone: 'UTC',
          };

          let formattedDate = date
            .toLocaleString(locale, options)
            .replace(',', '');
          let s = `<b>${formattedDate}</b><br/>`;

          this?.points?.forEach((point) => {
            s += `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y, 4)}</b><br/>`;
          });

          // Detectar si hay un punto individual (scatter no compartido)
          if (this.point && !this.points) {
            s += `<span style="color:${this.point.series.color}">\u25CF</span> ${this.point.series.name}: <b>${Highcharts.numberFormat(this.point.y, 4)}</b><br/>`;
          }

          return s;
        },
      },
      legend: {
        itemStyle: { fontSize: '16px', fontWeight: 'bold' },
      },
      global: {
        useUTC: true,
      },
      time: {
        useUTC: true,
      },
      series: [
        {
          turboThreshold: 0,
          name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 1',
          data: dataChart.map((d) => [d.datetime.getTime(), d.l1]),
          color: colors[0],
          marker: {
            enabled: false,
            states: {
              hover: {
                enables: true,
                radius: 5,
              },
            },
          },
        },
        {
          turboThreshold: 0,
          name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 2',
          data: dataChart.map((d) => [d.datetime.getTime(), d.l2]),
          color: colors[1],
          marker: {
            enabled: false,
            states: {
              hover: {
                enables: true,
                radius: 5,
              },
            },
          },
        },
        {
          turboThreshold: 0,
          name: intl.formatMessage({ id: 'app.deepview.current' }) + ' 3',
          data: dataChart.map((d) => [d.datetime.getTime(), d.l3]),
          color: colors[2],
          marker: {
            enabled: false,
            states: {
              hover: {
                enables: true,
                radius: 5,
              },
            },
          },
        },
        ...scatterSeries,
      ],
    }),
    [dataChart, title, intl]
  );

  const calculateStats = (key) => {
    const values = dataChart.map((d) => d[key]);
    if (!values.length) return { min: 0, max: 0, avg: 0 };
    const min = Math.min(...values).toFixed(4);
    const max = Math.max(...values).toFixed(4);
    const avg = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(4);
    return { min, max, avg };
  };

  const labelTitle =
    title === TITLES.PST
      ? 'Fluctuaciones de Tensión a Corto Plazo'
      : title === TITLES.PLT
        ? 'Fluctuaciones de Tensión a Largo Plazo'
        : title === TITLES.THDC
          ? 'THD Corriente'
          : 'THD Voltaje';

  return (
    <Box sx={{ width: '100%' }} ref={chartRef}>
      <Portlet>
        <PortletHeader noDivider={true}>
          <PortletLabel title={labelTitle} />
          <PortletToolbar>
            <Box display="flex" gap={1}>
              <Download name={labelTitle} itemRef={chartRef} />
              <ShareButton itemRef={chartRef} name={labelTitle} />
            </Box>
          </PortletToolbar>
        </PortletHeader>
        <PortletContent>
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </PortletContent>
      </Portlet>
      <Box width="100%" mt={3}>
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          gap={3}
        >
          <Chip
            label={labelTitle}
            sx={{
              fontWeight: 700,
              backgroundColor: '#730065',
              fontSize: '16px',
              color: 'white',
            }}
          />
          <Table sx={{ maxWidth: 650, boxShadow: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>{''}</TableCell>
                <TableCell align="center">Minímo</TableCell>
                <TableCell align="center">Máximo</TableCell>
                <TableCell align="center">Promedio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {['l1', 'l2', 'l3'].map((key, i) => {
                const stats = calculateStats(key);
                return (
                  <TableRow key={key}>
                    <TableCell align="center">
                      {intl.formatMessage({
                        id: 'app.deepview.current',
                      })}{' '}
                      {i + 1}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: '#8CBA42',
                        fontWeight: 'bold',
                        color: 'white',
                      }}
                    >
                      {stats.min}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: '#D64539',
                        fontWeight: 'bold',
                        color: 'white',
                      }}
                    >
                      {stats.max}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        bgcolor: '#EAAB36',
                        fontWeight: 'bold',
                        color: 'white',
                      }}
                    >
                      {stats.avg}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}
